import type { ApiKey, AuditEvent, ConnectedAccount } from "./types";

export interface Me {
  user: { id: string; name: string };
  organizations: {
    id: string;
    name: string;
    slug: string;
    role: string;
    projects: { id: string; name: string; slug: string }[];
  }[];
}

// Parse only fields the console actually returns. Never spread a wire object
// into a dashboard model (keys must never acquire a secret field).
function object(value: unknown): Record<string, unknown> {
  if (!value || typeof value !== "object" || Array.isArray(value))
    throw new Error("Expected an object");
  return value as Record<string, unknown>;
}
function string(value: unknown): string {
  if (typeof value !== "string") throw new Error("Expected a string");
  return value;
}
function nullable(value: unknown): string | null {
  return value === null ? null : string(value);
}
function strings(value: unknown): string[] {
  if (!Array.isArray(value)) throw new Error("Expected an array");
  return value.map(string);
}
function number(value: unknown): number {
  if (typeof value !== "number" || !Number.isFinite(value))
    throw new Error("Expected a number");
  return value;
}
function enumeration<T extends string>(value: unknown, allowed: readonly T[]): T {
  const parsed = string(value);
  const result = allowed.find((item) => item === parsed);
  if (!result) throw new Error("Unexpected enum value");
  return result;
}
export function items<T>(value: unknown, parse: (row: unknown) => T): T[] {
  const rows = object(value).items;
  if (!Array.isArray(rows)) throw new Error("Expected items");
  return rows.map(parse);
}
export function me(value: unknown): Me {
  const row = object(value);
  const user = object(row.user);
  if (!Array.isArray(row.organizations)) throw new Error("Expected organizations");
  return {
    user: { id: string(user.id), name: string(user.name) },
    organizations: row.organizations.map((value) => {
      const org = object(value);
      if (!Array.isArray(org.projects)) throw new Error("Expected projects");
      return {
        id: string(org.id),
        name: string(org.name),
        slug: string(org.slug),
        role: string(org.role),
        projects: org.projects.map((value) => {
          const project = object(value);
          return {
            id: string(project.id),
            name: string(project.name),
            slug: string(project.slug),
          };
        }),
      };
    }),
  };
}
/**
 * The console reports an import as `progress`, `reachedAt` and `itemsImported`,
 * and all three were being dropped here — so even after the API was fixed to
 * keep reporting a finished import, the dashboard would still have shown
 * nothing. Absent stays absent: a missing block means nothing was ever
 * imported, never a zeroed one, because a zero would claim a walk ran and
 * found an empty mailbox.
 */
function backfill(value: unknown): ConnectedAccount["backfill"] {
  if (value == null) return undefined;
  const row = object(value);
  const imported = number(row.itemsImported ?? row.messagesImported);
  return {
    /**
     * The ONE inference in the dashboard, and it lives here rather than in two
     * renderers. The console is gaining an explicit `complete`; until every
     * deployed revision sends it, a null `progress` is the signal, since
     * `finish()` nulls it so the bar comes down. Preferring the flag where it
     * exists means neither side needs a flag day, and when the field is
     * everywhere the fallback can go.
     */
    complete: typeof row.complete === "boolean" ? row.complete : row.progress == null,
    // Carried through as null rather than coerced: a 1 here would be a bar at
    // 100% that never comes down.
    progress: row.progress == null ? null : number(row.progress),
    reachedAt: string(row.reachedAt),
    chatsImported: row.chatsImported == null ? 0 : number(row.chatsImported),
    messagesImported: imported,
    // The console does not report a finish time today. Shown only if it ever does.
    completedAt: row.completedAt == null ? undefined : string(row.completedAt),
  };
}
export function account(value: unknown): ConnectedAccount {
  const row = object(value);
  if (!Array.isArray(row.budget)) throw new Error("Expected budget windows");
  const attention = row.attention == null ? undefined : object(row.attention);
  return {
    id: string(row.id),
    provider: string(row.provider),
    handle: string(row.handle),
    status: enumeration(row.status, [
      "connected",
      "syncing",
      "needs_attention",
      "paused",
      "disconnected",
    ]),
    auth: enumeration(row.auth, ["oauth", "imap", "dav", "phone", "account"]),
    lastSyncAt: nullable(row.lastSyncAt),
    attention: attention
      ? {
          code: string(attention.code),
          message: nullable(attention.message),
          since: nullable(attention.since),
        }
      : undefined,
    budget: {
      windows: row.budget.map((value) => {
        const window = object(value);
        return {
          label: string(window.label),
          used: number(window.used),
          limit: number(window.limit),
          resetsAt: string(window.resetsAt),
        };
      }),
    },
    backfill: backfill(row.backfill),
  };
}
export function apiKey(value: unknown): ApiKey {
  const row = object(value);
  return {
    id: string(row.id),
    name: string(row.name),
    environment: enumeration(row.environment, ["sandbox", "production"]),
    prefix: string(row.prefix),
    createdAt: string(row.createdAt),
    lastUsedAt: row.lastUsedAt == null ? undefined : string(row.lastUsedAt),
    scopes: row.scopes == null ? undefined : strings(row.scopes),
  };
}
export function auditEvent(value: unknown): AuditEvent {
  const row = object(value);
  return {
    id: string(row.id),
    at: string(row.at),
    actor: string(row.actor),
    actorKind: enumeration(row.actorKind, ["user", "api_key", "system"]),
    action: string(row.action),
    target: string(row.target),
    summary: string(row.summary),
    requestId: string(row.requestId),
  };
}
